import React from "react";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import { getPages } from "../../common/pages/pages";
import { formatUtcDateTime } from "../../common/dateTime";
import { formatRublesCompact } from "../../common/money";
import { NotificationService } from "../../common/notification";
import {
    AdCampaign,
    AdCampaignStatus,
    AdCampaignStatusLabels,
} from "../../models/adCampaign";
import { adCampaignsListApi } from "./adCampaignsApi";

function statusColor(status: AdCampaignStatus): "neutral" | "success" | "warning" | "danger" {
    switch (status) {
        case AdCampaignStatus.Active:
            return "success";
        case AdCampaignStatus.Paused:
            return "warning";
        case AdCampaignStatus.Finished:
            return "neutral";
        default:
            return "danger";
    }
}

function CampaignCard(props: {
    campaign: AdCampaign;
    onCopy: (url: string) => void;
    onEdit: () => void;
}) {
    const { campaign: c, onCopy, onEdit } = props;
    return (
        <Box
            sx={{
                bgcolor: "background.level1",
                borderRadius: "md",
                p: 2,
                mb: 1.5,
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ mb: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        level="title-sm"
                        fontWeight="lg"
                        sx={{ wordBreak: "break-word" }}
                    >
                        {c.name}
                    </Typography>
                    <Typography level="body-xs" sx={{ fontFamily: "monospace", color: "neutral.500" }}>
                        {c.code}
                    </Typography>
                </Box>
                <Chip size="sm" color={statusColor(c.status)} variant="soft" sx={{ flexShrink: 0 }}>
                    {AdCampaignStatusLabels[c.status]}
                </Chip>
            </Stack>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                    mb: 1.5,
                }}
            >
                <Box>
                    <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                        Канал
                    </Typography>
                    <Typography level="body-sm">{c.channel}</Typography>
                </Box>
                <Box>
                    <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                        Spend
                    </Typography>
                    <Typography level="body-sm">
                        {c.spendKopecks != null ? formatRublesCompact(c.spendKopecks) : "—"}
                    </Typography>
                </Box>
                <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                        Период
                    </Typography>
                    <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                        {formatUtcDateTime(c.startAt)}
                        {c.endAt ? ` — ${formatUtcDateTime(c.endAt)}` : " — ∞"}
                    </Typography>
                </Box>
                {c.promoCode && (
                    <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                            Промокод
                        </Typography>
                        <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>
                            {c.promoCode}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack direction="row" justifyContent="flex-end" gap={0.75}>
                <Tooltip title="Копировать ссылку">
                    <IconButton size="sm" variant="outlined" onClick={() => onCopy(c.trackingUrl)}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Редактировать">
                    <IconButton size="sm" variant="outlined" onClick={onEdit}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}

export function AdCampaignsList() {
    const navigate = useNavigate();
    const [items, setItems] = React.useState<AdCampaign[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [search, setSearch] = React.useState("");
    const [status, setStatus] = React.useState<AdCampaignStatus | null>(null);
    const [loading, setLoading] = React.useState(false);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await adCampaignsListApi(page, 30, status, search);
            if (result.result && result.value) {
                setItems(result.value.items);
                setPageCount(Math.max(1, Math.ceil(result.value.total / 30)));
            } else {
                NotificationService.alertError("Не удалось загрузить кампании");
            }
        } finally {
            setLoading(false);
        }
    }, [page, status, search]);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [loadData]);

    const copyUrl = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            NotificationService.alertSuccess("Ссылка скопирована");
        } catch {
            NotificationService.alertError("Не удалось скопировать");
        }
    };

    const emptyLabel = loading ? "Загрузка…" : "Кампаний пока нет";

    return (
        <Box sx={{ display: "flex", minHeight: "100dvh" }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: "calc(12px + var(--Header-height))",
                        sm: "calc(12px + var(--Header-height))",
                        md: 3,
                    },
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    height: "100dvh",
                    gap: 1.5,
                    overflow: "auto",
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                >
                    <Typography level="h2" component="h1" sx={{ fontSize: { xs: "xl2", md: "xl3" } }}>
                        Рекламные кампании
                    </Typography>
                    <Button
                        startDecorator={<AddIcon />}
                        onClick={() => navigate("/app/ad-campaigns/new")}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                        Создать
                    </Button>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ sm: "center" }}>
                    <Input
                        placeholder="Поиск: название, code, промокод, канал"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, minWidth: 0, width: "100%" }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                if (page !== 1) setPage(1);
                                else loadData();
                            }
                        }}
                    />
                    <Select
                        placeholder="Статус"
                        value={status}
                        onChange={(_, v) => {
                            setStatus(typeof v === "number" ? (v as AdCampaignStatus) : null);
                            setPage(1);
                        }}
                        sx={{ minWidth: { xs: "100%", sm: 160 }, width: { xs: "100%", sm: "auto" } }}
                    >
                        <Option value={null}>Все</Option>
                        {Object.values(AdCampaignStatus)
                            .filter((v) => typeof v === "number")
                            .map((v) => (
                                <Option key={v} value={v as AdCampaignStatus}>
                                    {AdCampaignStatusLabels[v as AdCampaignStatus]}
                                </Option>
                            ))}
                    </Select>
                    <Button
                        variant="outlined"
                        loading={loading}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                        onClick={() => {
                            if (page !== 1) setPage(1);
                            else loadData();
                        }}
                    >
                        Найти
                    </Button>
                </Stack>

                {/* Desktop: таблица */}
                <Sheet
                    variant="outlined"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        borderRadius: "sm",
                        overflow: "auto",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Table size="sm" stickyHeader hoverRow sx={{ minWidth: 720 }}>
                        <thead>
                            <tr>
                                <th>Название</th>
                                <th>Канал</th>
                                <th>Статус</th>
                                <th>Период</th>
                                <th>Промокод</th>
                                <th style={{ textAlign: "right" }}>Spend</th>
                                <th>Ссылка</th>
                                <th style={{ width: 72 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={8}>
                                        <Typography level="body-sm" sx={{ py: 2 }}>
                                            {emptyLabel}
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                            {items.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <Typography level="title-sm">{c.name}</Typography>
                                        <Typography level="body-xs" sx={{ fontFamily: "monospace" }}>
                                            {c.code}
                                        </Typography>
                                    </td>
                                    <td>{c.channel}</td>
                                    <td>
                                        <Chip size="sm" color={statusColor(c.status)} variant="soft">
                                            {AdCampaignStatusLabels[c.status]}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">
                                            {formatUtcDateTime(c.startAt)}
                                            {c.endAt ? ` — ${formatUtcDateTime(c.endAt)}` : " — ∞"}
                                        </Typography>
                                    </td>
                                    <td>{c.promoCode || "—"}</td>
                                    <td style={{ textAlign: "right" }}>
                                        {c.spendKopecks != null ? formatRublesCompact(c.spendKopecks) : "—"}
                                    </td>
                                    <td>
                                        <Tooltip title={c.trackingUrl}>
                                            <IconButton size="sm" variant="plain" onClick={() => copyUrl(c.trackingUrl)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            onClick={() =>
                                                navigate(`/app/ad-campaigns/${c.id}/edit`, { state: { campaign: c } })
                                            }
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>

                {/* Mobile: карточки */}
                <Box sx={{ display: { xs: "block", sm: "none" }, flex: 1 }}>
                    {items.length === 0 ? (
                        <Typography level="body-sm" sx={{ py: 2 }}>
                            {emptyLabel}
                        </Typography>
                    ) : (
                        items.map((c) => (
                            <CampaignCard
                                key={c.id}
                                campaign={c}
                                onCopy={copyUrl}
                                onEdit={() =>
                                    navigate(`/app/ad-campaigns/${c.id}/edit`, { state: { campaign: c } })
                                }
                            />
                        ))
                    )}
                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                    flexWrap="wrap"
                    sx={{ pb: { xs: 1, sm: 0 } }}
                >
                    <IconButton
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButton>
                    {getPages(pageCount, page).map((p, i) =>
                        p === "..." ? (
                            <Typography key={`e${i}`} level="body-sm">
                                …
                            </Typography>
                        ) : (
                            <Button
                                key={`${p}-${i}`}
                                size="sm"
                                variant={Number(p) === page ? "solid" : "plain"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </Button>
                        )
                    )}
                    <IconButton
                        size="sm"
                        disabled={page >= pageCount}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    >
                        <KeyboardArrowRightIcon />
                    </IconButton>
                </Stack>
            </Box>
        </Box>
    );
}
